import { Stethoscope } from 'lucide-react';
import { Drawer } from '~/shared/components/Drawer';
import { Button } from '~/shared/components/buttons/Button';
import { DataCell } from '~/shared/DataCell';
import { formatPhoneNumber } from '~/util/functions';
import { OrderDeliveryMethodEnum, type FullOrderViewModel } from '@generated';
import { FrequencyOrders, hasFrequencyOrders } from '../../frequencies/FrequencyOrders';
import { LabOrders } from '../../frequencies/labs/LabOrders';
import { hasWoundOrders, WoundOrders } from '../../wounds/orders/WoundOrders';
import { GoalsAndInterventionOrders, hasGoalsAndInterventionsOrders } from '../../careplanning/goals-interventions/orders/GoalsAndInterventionOrders';
import { FullOrderContextProvider } from '~/features/orders/orders/FullOrderContext';

export function VisitOrderDrawer({ fullOrder }: { fullOrder: FullOrderViewModel }) {
	return (
		<Button
			aria-label="View Order"
			Icon={Stethoscope}
			className="rounded-lg p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
			useSuspense
			renderModal={closeModal => (
				<Drawer
					size="half"
					isOpen
					closeDrawer={() => closeModal()}
					onOpenChange={() => closeModal()}
					title="Order Quick View"
					status={`${fullOrder.orderTypeLabel} - ${fullOrder.orderingPatientPhysician.physician.fullName}`}
				>
					<FullOrderContextProvider fullOrder={fullOrder} readOnly>
						<div className="p-4">
							<div className="flex-1 space-y-8 overflow-y-auto">
								<div className="mb-3 overflow-hidden rounded-lg border border-gray-200">
									<div className="grid grid-cols-3 divide-x divide-gray-200">
										<DataCell label="Order Type" value={fullOrder.orderTypeLabel} />
										<DataCell label="Order Date" value={fullOrder.orderDate.toLocaleDateString()} />
										<DataCell label="Physician" value={fullOrder.orderingPatientPhysician.physician.fullName} />
									</div>
									<div className="grid grid-cols-3 divide-x divide-gray-200 border-t border-gray-200">
										<DataCell
											label="Physician Phone"
											value={formatPhoneNumber(fullOrder.orderingPatientPhysician.physician.phoneNumber)}
										/>
										<DataCell
											label="Physician Fax"
											value={
												fullOrder.orderingPatientPhysician.physician.orderDeliveryMethodId === OrderDeliveryMethodEnum.Fax
													? formatPhoneNumber(fullOrder.orderingPatientPhysician.physician.faxNumber)
													: `N/A (${fullOrder.orderingPatientPhysician.physician.orderDeliveryMethodLabel})`
											}
										/>
										<DataCell
											label="Physician Address"
											value={
												fullOrder.orderingPatientPhysician.physician.address
													? fullOrder.orderingPatientPhysician.physician.address.formattedAddress
													: 'N/A'
											}
										/>
									</div>
								</div>
								{hasFrequencyOrders(fullOrder) && <FrequencyOrders />}
								{fullOrder.labs.length > 0 && <LabOrders />}
								{hasGoalsAndInterventionsOrders(fullOrder) && <GoalsAndInterventionOrders />}
								{hasWoundOrders(fullOrder) && <WoundOrders />}
							</div>
						</div>
					</FullOrderContextProvider>
				</Drawer>
			)}
		/>
	);
}
